import React, { useState } from 'react';

import dompurify from 'dompurify';
import styled, { css } from 'styled-components';

import type { FormResponses, FormResult, SigninRequirements, SignoutRequirements } from '@sine/sine-core-api-types';
import {
  ButtonSelect,
  CheckBoxGroup,
  Form,
  Input,
  type JoiForm,
  SingleSelect,
  spacing,
  useJoiForm,
} from '@sine/sine-ui-components';

import { FieldType } from '../../../services/form/constants';
import { useCheckInOutForm } from '../hooks/useCheckInOutForm';
import { type CheckInFormValues } from './visitorCheckInModal.types';

const marginBottom = css`
  &:not(:last-child) {
    margin-bottom: ${spacing(2)};
  }
`;

const StyledImg = styled.img`
  max-width: 600px;
  ${marginBottom}
`;

const StyledDiv = styled.div`
  ${marginBottom}
`;

const booleanSwitchOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

interface CheckInFormProps {
  formRef: React.MutableRefObject<JoiForm<CheckInFormValues> | undefined>;
  prefilledForm?: FormResponses | FormResult | null;
  hideForm?: boolean;
  siteRequirements?: SigninRequirements | SignoutRequirements;
  visitorTypeId: string;
}

export default function SiteCheckInForm({
  formRef,
  prefilledForm,
  hideForm,
  siteRequirements,
  visitorTypeId,
}: CheckInFormProps) {
  const [selectiveChoiceValues, setSelectiveChoiceValues] = useState<Array<string> | undefined>([]);

  const { formFieldsFilteredByVisitorType, schema, initialValues } = useCheckInOutForm(
    siteRequirements,
    visitorTypeId,
    prefilledForm,
    selectiveChoiceValues,
  );

  formRef.current = useJoiForm(schema, initialValues);

  const checkInForm = formRef.current;

  if (hideForm) {
    return null;
  }

  return (
    <Form
      formSpacing='compact'
      values={checkInForm.values}
      errors={checkInForm.errors}
      onFieldChange={(name, value) => checkInForm.onChange(name, value, false)}
      onFieldBlur={checkInForm.onBlur}
    >
      {formFieldsFilteredByVisitorType?.map(({ id, type, label, required, options }) => {
        switch (type) {
          case FieldType.TEXT:
            return (
              <Input key={id} name={id} label={`${label}${required ? ' *' : ''}`} data-st-ext={`input-text-${id}`} />
            );
          case FieldType.NUMBER:
            return (
              <Input key={id} name={id} label={`${label}${required ? ' *' : ''}`} data-st-ext={`input-number-${id}`} />
            );
          case FieldType.BOOLEAN_SWITCH:
            return (
              <ButtonSelect
                key={id}
                name={id}
                label={`${label}${required ? ' *' : ''}`}
                /**
                 * Note: ButtonSelect value must be a boolean, not a string, for the BOOLEAN_SWITCH field type.
                 */
                options={booleanSwitchOptions as any}
                onChange={(value) => checkInForm.onChange(id, value, true)}
                data-st-ext={`input-boolean-switch-${id}`}
              />
            );
          case FieldType.MULTIPLE_CHOICE:
            return (
              <SingleSelect
                key={id}
                name={id}
                label={`${label}${required ? ' *' : ''}`}
                options={options?.map((option, index) => ({ label: option, value: index })) || []}
                onChange={(value) => {
                  checkInForm.onChange(id, value, true);
                }}
                isClearable
                data-st-ext={`input-multiple-choice-${id}`}
              />
            );
          case FieldType.SELECTIVE_CHOICE:
            return (
              <CheckBoxGroup
                key={id}
                name={id}
                label={`${label}${required ? ' *' : ''}`}
                formSpacing='compact'
                options={options?.map((option) => ({ key: option, label: option }))}
                data-st-ext={`input-selective-choice-${id}`}
                onChange={(value) => {
                  setSelectiveChoiceValues(value);
                  checkInForm.onChange(id, value, true);
                }}
              />
            );
          case FieldType.INFO:
            return (
              // We can ensure that this is from the server side
              // for anyone does not know about it in Code Review:
              // https://reactjs.org/docs/dom-elements.html#dangerouslysetinnerhtml
              <StyledDiv
                key={id}
                dangerouslySetInnerHTML={{ __html: dompurify.sanitize(label) }}
                data-st-ext={`info-${id}`}
              />
            );
          case FieldType.IMAGE:
            return <StyledImg key={id} src={label} alt={label} data-st-ext={`image-${id}`} />;
          case FieldType.NDA:
            return (
              <StyledDiv
                key={id}
                dangerouslySetInnerHTML={{ __html: dompurify.sanitize(label) }}
                data-st-ext={`nda-${id}`}
              />
            );
        }
      })}
    </Form>
  );
}
